import { AlertTriangle } from 'lucide-react'
import { useRef, type KeyboardEvent } from 'react'

import './browse.css'

export interface ChoiceChip {
  id: string
  label: string
  /** Число рядом с подписью («Возвраты 3»); `undefined` — без числа. */
  count?: number
  /** Тревога на чипе: значок ⚠ и число («Настройки ⚠ 2»); нужен, когда внимание требуется, даже если чип не выбран. `undefined` или 0 — без значка. */
  alert?: number
}

/**
 * Быстрый отбор «одно из»: «Все · Продажи · Возвраты». Радиогруппа (стрелки двигают выбор), выбранный чип — рамка,
 * жирность и `aria-checked`, а не только цвет. В отличие от `Tabs` панели содержимого у чипов нет: они меняют
 * отбор списка рядом, а не показывают другой экран.
 */
export function ChoiceChips({
  label,
  items,
  value,
  onChange,
}: {
  label: string
  items: readonly ChoiceChip[]
  value: string
  onChange: (id: string) => void
}) {
  const refs = useRef(new Map<string, HTMLButtonElement>())
  const move = (event: KeyboardEvent<HTMLButtonElement>, from: string) => {
    const at = items.findIndex((item) => item.id === from)
    const next =
      event.key === 'ArrowRight' || event.key === 'ArrowDown'
        ? items[(at + 1) % items.length]
        : event.key === 'ArrowLeft' || event.key === 'ArrowUp'
          ? items[(at - 1 + items.length) % items.length]
          : undefined
    if (next === undefined) return
    event.preventDefault()
    onChange(next.id)
    refs.current.get(next.id)?.focus()
  }
  return (
    <div className="krp-chips" role="radiogroup" aria-label={label}>
      {items.map((item) => {
        const checked = item.id === value
        return (
          <button
            key={item.id}
            ref={(node) => {
              if (node === null) refs.current.delete(item.id)
              else refs.current.set(item.id, node)
            }}
            type="button"
            role="radio"
            aria-checked={checked}
            tabIndex={checked ? 0 : -1}
            className="krp-chip"
            onClick={() => onChange(item.id)}
            onKeyDown={(event) => move(event, item.id)}
          >
            {item.label}
            {item.count !== undefined && <span className="krp-chip__count">{item.count}</span>}
            {item.alert !== undefined && item.alert > 0 && (
              <span className="krp-chip__alert" role="img" aria-label={`Требует внимания: ${item.alert}`}>
                <AlertTriangle size={12} aria-hidden="true" />
                {item.alert}
              </span>
            )}
          </button>
        )
      })}
    </div>
  )
}
